import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { Plus, X } from "lucide-react";
import CreateExpensePage from "./CreateExpensePage";
import api from "../Helper/api";
import { openProtectedFile } from "../Helper/openProtectedFile";
import html2pdf from "html2pdf.js";

export default function ExpenseList() {
  const auth = useSelector((state) => state.auth.loginUser);
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Filtreler
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Detay Modal State'leri
  const [selectedExpense, setSelectedExpense] = useState(null);

  const isAdmin = auth?.userType === "admin";

  useEffect(() => {
    fetchExpenses();
  }, [auth]);

  useEffect(() => {
    handleFilter();
  }, [expenses, filterName, filterStatus]);

  const fetchExpenses = async () => {
    if (!auth) return;
    setLoading(true);
    try {
      const endpoint = isAdmin ? "/expenses" : "/expenses/my";
      const response = await api.get(endpoint);
      if (response.data?.state) {
        setExpenses(response.data.expenses);
      }
    } catch (error) {
      console.error(error);
      toast.error("Masraflar yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    let result = expenses || [];

    if (filterName && isAdmin) {
      const lower = filterName.toLowerCase();
      result = result.filter((exp) => {
        const fullName = exp.fullName?.toLowerCase() || "";
        const tc = exp.owner?.tc?.toLowerCase() || "";
        return fullName.includes(lower) || tc.includes(lower);
      });
    }

    if (filterStatus) {
      result = result.filter((exp) => exp.status === filterStatus);
    }

    setFilteredExpenses(result);
  };

  const handleResetFilters = () => {
    setFilterName("");
    setFilterStatus("");
  };

  const handleApprove = async (id) => {
    const confirmed = window.confirm("Bu masraf talebini onaylamak istediğinize emin misiniz?");
    if (!confirmed) return;

    try {
      const response = await api.patch(`/expenses/approve/${id}`);
      if (response.data?.state) {
        toast.success("Masraf talebi onaylandı.");
        fetchExpenses();
        if (selectedExpense?._id === id) {
          setSelectedExpense((prev) => ({ ...prev, status: "APPROVED" }));
        }
      }
    } catch (error) {
      toast.error("Onaylama işlemi başarısız.");
    }
  };

  const handleReject = async (id) => {
    const confirmed = window.confirm("Bu masraf talebini reddetmek istediğinize emin misiniz?");
    if (!confirmed) return;

    try {
      const response = await api.patch(`/expenses/reject/${id}`);
      if (response.data?.state) {
        toast.success("Masraf talebi reddedildi.");
        fetchExpenses();
        if (selectedExpense?._id === id) {
          setSelectedExpense((prev) => ({ ...prev, status: "REJECTED" }));
        }
      }
    } catch (error) {
      toast.error("Reddetme işlemi başarısız.");
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm("Bu masraf talebini silmek istediğinize emin misiniz?");
    if (!confirmed) return;

    try {
      const response = await api.delete(`/expenses/${id}`);
      if (response.data?.state) {
        toast.success("Masraf talebi silindi.");
        fetchExpenses();
        if (selectedExpense?._id === id) {
          setSelectedExpense(null);
        }
      }
    } catch (error) {
      toast.error("Silme işlemi başarısız.");
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "APPROVED":
        return "bg-green-100 text-green-800 border-green-200";
      case "REJECTED":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "APPROVED":
        return "Onaylandı";
      case "REJECTED":
        return "Reddedildi";
      default:
        return "Beklemede";
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById("expense-print-area");
    if (!element) return;

    const opt = {
      margin:       [0.4, 0.4, 0.4, 0.4], // in inches [top, left, bottom, right]
      filename:     `Masraf_Formu_${selectedExpense._id}.pdf`,
      image:        { type: "jpeg", quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true, logging: false },
      jsPDF:        { unit: "in", format: "a4", orientation: "portrait" }
    };

    html2pdf().set(opt).from(element).save();
  };

  return (
    <>
      <div className="max-w-6xl mx-auto p-6 space-y-6 print:p-0 print:max-w-full">
      {/* Arayüz: Yazdırma esnasında tamamen gizlenir */}
      <div className="print:hidden space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-gray-800">
            {isAdmin ? "Masraf Onay Paneli" : "Masraf Taleplerim"}
          </h1>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 shadow"
          >
            <Plus size={18} />
            Masraf Ekle
          </button>
        </div>

        {/* Filtre Alanı */}
        <div className="bg-white p-4 rounded-lg shadow flex flex-wrap gap-4 items-end">
          {isAdmin && (
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Personel Ad Soyad / TC</label>
              <input
                type="text"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                placeholder="Arama yapın..."
                className="w-full border rounded px-3 py-1.5 text-sm"
              />
            </div>
          )}
          <div className="w-[180px]">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Durum</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full border rounded px-3 py-1.5 text-sm"
            >
              <option value="">Tümü</option>
              <option value="PENDING">Beklemede</option>
              <option value="APPROVED">Onaylandı</option>
              <option value="REJECTED">Reddedildi</option>
            </select>
          </div>
          <button
            onClick={handleResetFilters}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm font-medium transition-colors"
          >
            Temizle
          </button>
        </div>

        {/* Masraf Tablosu */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Yükleniyor...</div>
          ) : filteredExpenses.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Kayıtlı masraf talebi bulunamadı.</div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b bg-gray-50 text-xs font-bold text-gray-600 uppercase">
                  {isAdmin && <th className="py-3 px-4">Talep Eden</th>}
                  <th className="py-3 px-4">Banka Alıcısı</th>
                  <th className="py-3 px-4">IBAN</th>
                  <th className="py-3 px-4 text-right">Toplam Tutar</th>
                  <th className="py-3 px-4">Tarih</th>
                  <th className="py-3 px-4 text-center">Durum</th>
                  <th className="py-3 px-4 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map((exp) => (
                  <tr key={exp._id} className="border-b text-sm text-gray-700 hover:bg-gray-50">
                    {isAdmin && (
                      <td className="py-3 px-4">
                        <div className="font-semibold">{exp.owner?.name} {exp.owner?.surname}</div>
                        <div className="text-xs text-gray-500">{exp.owner?.tc}</div>
                      </td>
                    )}
                    <td className="py-3 px-4 font-medium">{exp.fullName}</td>
                    <td className="py-3 px-4 text-xs font-mono">TR{exp.iban}</td>
                    <td className="py-3 px-4 text-right font-semibold">
                      {exp.totalAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL
                    </td>
                    <td className="py-3 px-4">
                      {new Date(exp.createdAt).toLocaleDateString("tr-TR")}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${getStatusBadgeClass(exp.status)}`}>
                        {getStatusText(exp.status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right space-x-1 whitespace-nowrap">
                      <button
                        onClick={() => setSelectedExpense(exp)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-2.5 py-1 rounded text-xs transition-colors"
                      >
                        Detay
                      </button>

                      {isAdmin && exp.status === "PENDING" && (
                        <>
                          <button
                            onClick={() => handleApprove(exp._id)}
                            className="bg-green-600 hover:bg-green-700 text-white px-2.5 py-1 rounded text-xs transition-colors"
                          >
                            Onayla
                          </button>
                          <button
                            onClick={() => handleReject(exp._id)}
                            className="bg-red-500 hover:bg-red-600 text-white px-2.5 py-1 rounded text-xs transition-colors"
                          >
                            Reddet
                          </button>
                        </>
                      )}

                      {(!isAdmin && exp.status === "PENDING" || isAdmin) && (
                        <button
                          onClick={() => handleDelete(exp._id)}
                          className="bg-gray-100 hover:bg-gray-200 text-red-600 px-2.5 py-1 rounded text-xs border transition-colors"
                        >
                          Sil
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      </div>

      {/* DETAY VE YAZDIRMA EKRANI (Modal benzeri, selectedExpense varsa açılır) */}
      {selectedExpense && (
        <div 
          onClick={() => setSelectedExpense(null)}
          className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-start justify-center p-4 pt-10 print:relative print:bg-transparent print:p-0 print:z-0"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-4xl w-full p-6 relative border shadow-2xl print:shadow-none print:border-none print:p-0 print:max-w-full"
          >
            <button
              onClick={() => setSelectedExpense(null)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors print:hidden"
              title="Kapat"
            >
              <X size={24} />
            </button>
            <div className="flex justify-between items-center border-b pb-4 print:hidden">
              <h2 className="text-xl font-semibold text-gray-800">Masraf Formu Detayı</h2>
              <div className="space-x-2 flex items-center pr-8">
                <button
                  onClick={handlePrint}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium text-sm transition-colors"
                >
                  Yazdır (A4 Çıktı)
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium text-sm transition-colors"
                >
                  PDF İndir
                </button>
              </div>
            </div>

            {/* A4 STANDARTLARINDA YAZDIRILABİLİR ŞABLON */}
            <div id="expense-print-area" className="print-area bg-white text-black font-sans max-w-[21cm] mx-auto print:max-w-full p-6">
              <div className="flex justify-between items-center border-b-2 border-black pb-4 mb-6">
                <div>
                  <h1 className="text-2xl font-bold uppercase tracking-wider text-gray-900">PAVZA BİLİŞİM</h1>
                  <p className="text-xs text-gray-500">Personel Masraf Bildirim Formu</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold">Tarih: {new Date(selectedExpense.createdAt).toLocaleDateString("tr-TR")}</p>
                  <p className="text-xs font-mono text-gray-600">Form ID: {selectedExpense._id}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6 border-b pb-4">
                <div>
                  <p className="text-sm"><strong>Personel Ad Soyad:</strong> {selectedExpense.fullName}</p>
                  <p className="text-sm"><strong>TC Kimlik No:</strong> {selectedExpense.owner?.tc}</p>
                </div>
                <div>
                  <p className="text-sm"><strong>Banka IBAN:</strong> TR{selectedExpense.iban}</p>
                  <p className="text-sm"><strong>Talep Durumu:</strong> {getStatusText(selectedExpense.status)}</p>
                </div>
              </div>

              <table className="w-full text-left border-collapse border border-black mb-8">
                <thead>
                  <tr className="bg-gray-100 border-b border-black text-xs font-bold uppercase text-gray-800">
                    <th className="border border-black py-2 px-3">Belge No</th>
                    <th className="border border-black py-2 px-3">Tarih</th>
                    <th className="border border-black py-2 px-3">Tip</th>
                    <th className="border border-black py-2 px-3">Tür</th>
                    <th className="border border-black py-2 px-3">Açıklama</th>
                    <th className="border border-black py-2 px-3">Belge Görseli</th>
                    <th className="border border-black py-2 px-3 text-right">Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedExpense.items.map((item, index) => (
                    <tr key={index} className="border-b border-black text-sm">
                      <td className="border border-black py-2 px-3">{item.documentNo}</td>
                      <td className="border border-black py-2 px-3">{new Date(item.expenseDate).toLocaleDateString("tr-TR")}</td>
                      <td className="border border-black py-2 px-3">{item.documentType}</td>
                      <td className="border border-black py-2 px-3">{item.expenseType}</td>
                      <td className="border border-black py-2 px-3">{item.description || "-"}</td>
                      <td className="border border-black py-2 px-3">
                        {item.documentUrl ? (
                          <button
                            type="button"
                            onClick={() => openProtectedFile(item.documentUrl)}
                            className="text-blue-600 hover:underline font-medium print:text-black"
                          >
                            Görüntüle
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="border border-black py-2 px-3 text-right">{item.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL</td>
                    </tr>
                  ))}
                  <tr className="font-bold text-base bg-gray-50">
                    <td colSpan="6" className="border border-black py-3 px-3 text-right">Toplam Tutar:</td>
                    <td className="border border-black py-3 px-3 text-right">
                      {selectedExpense.totalAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* İmzalar */}
              <div className="grid grid-cols-2 gap-8 mt-16 text-center">
                <div>
                  <p className="font-bold border-b border-black pb-2 mb-2">Talep Eden Personel</p>
                  <p className="text-sm">{selectedExpense.fullName}</p>
                  <p className="text-xs text-gray-500 mt-4">(İmza)</p>
                </div>
                <div>
                  <p className="font-bold border-b border-black pb-2 mb-2">Onaylayan Yönetici</p>
                  <p className="text-sm">Sistem Yöneticisi</p>
                  <p className="text-xs text-gray-500 mt-4">(İmza / Kaşe)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isCreateModalOpen && (
        <div 
          onClick={() => setIsCreateModalOpen(false)}
          className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-start justify-center p-4 pt-6 pb-12 print:relative print:bg-transparent print:p-0 print:z-0"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 relative border shadow-2xl print:shadow-none print:border-none print:p-0 print:max-w-full text-black"
          >
            <button
              onClick={() => setIsCreateModalOpen(false)}
              className="absolute top-4 right-4 z-20 text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-1 rounded-lg transition-colors print:hidden"
              title="Kapat"
            >
              <X size={22} />
            </button>
            <CreateExpensePage
              onCancel={() => setIsCreateModalOpen(false)}
              onSuccess={() => {
                setIsCreateModalOpen(false);
                fetchExpenses();
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
